import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('access_token'));
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const expirationTimerRef = useRef(null);

  const clearSession = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    if (expirationTimerRef.current) {
      clearTimeout(expirationTimerRef.current);
      expirationTimerRef.current = null;
    }
  }, []);

  const scheduleTokenExpiration = useCallback((accessToken) => {
    if (!accessToken) return;
    const payload = parseJwt(accessToken);
    if (!payload?.exp) return;
    const expiresAt = payload.exp * 1000;
    const delay = expiresAt - Date.now() - 5000; // 5s before expiry
    if (delay > 0) {
      if (expirationTimerRef.current) clearTimeout(expirationTimerRef.current);
      expirationTimerRef.current = setTimeout(() => {
        clearSession();
        if (!window.location.pathname.startsWith('/admin/login')) {
          window.location.href = '/admin/login';
        }
      }, delay);
    } else {
      clearSession();
    }
  }, [clearSession]);

  const checkAuth = useCallback(async () => {
    try {
      const storedToken = localStorage.getItem('access_token');
      if (!storedToken) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${storedToken}`
        }
      });
      setUser(response.data);
      setToken(storedToken);
      scheduleTokenExpiration(storedToken);
    } catch (error) {
      clearSession();
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL, clearSession, scheduleTokenExpiration]);

  useEffect(() => {
    checkAuth();
    return () => {
      if (expirationTimerRef.current) clearTimeout(expirationTimerRef.current);
    };
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { email, password }
      );

      const { access_token, refresh_token, user: userData } = response.data;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);

      setUser(userData);
      setToken(access_token);
      scheduleTokenExpiration(access_token);
      return { success: true };
    } catch (error) {
      let message;
      if (error.code === 'ERR_NETWORK' || !error.response) {
        message = 'Le serveur est temporairement indisponible. Vérifiez que le backend est démarré.';
      } else {
        message = error.response?.data?.detail || 'Erreur de connexion';
      }
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      const currentToken = localStorage.getItem('access_token');
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
        headers: currentToken ? { 'Authorization': `Bearer ${currentToken}` } : {}
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearSession();
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    checkAuth,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
